import React, { useState, useEffect } from 'react';
import './News.css';
import Navbar from '../../Navbar/Navbar';

const LOGIN_API_URL = 'https://lfsofficial.in/api/admin_login.php';
const LIST_API_URL = 'https://lfsofficial.in/api/admin_news_list.php';
const CREATE_API_URL = 'https://lfsofficial.in/api/admin_news_create.php';
const UPDATE_API_URL = 'https://lfsofficial.in/api/admin_news_update.php';
const DELETE_API_URL = 'https://lfsofficial.in/api/admin_news_delete.php';
const TOGGLE_PIN_API_URL = 'https://lfsofficial.in/api/admin_news_toggle_pin.php';

const CATEGORY_OPTIONS = [
  'All',
  'Announcements',
  'Academic',
  'Sports',
  'Cultural',
  'Achievements',
  'Events',
  'Admissions',
  'Other',
];

const CATEGORY_COLORS = {
  Announcements: '#e05c2a',
  Academic:      '#1a3a8c',
  Sports:        '#1a7a3a',
  Cultural:      '#7a1a6e',
  Achievements:  '#c89b00',
  Events:        '#0077aa',
  Admissions:    '#2255cc',
  Other:         '#555',
};

const TYPE_OPTIONS = ['News', 'Event'];

const emptyForm = {
  title: '', category: '', type: 'News',
  date: '', eventDate: '', venue: '',
  summary: '', body: '', coverImage: '',
  tags: '', pinned: false,
};

const NewsEvents = () => {
  const [posts, setPosts]             = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState('');

  const [filterCat, setFilterCat]     = useState('All');
  const [filterType, setFilterType]   = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewPost, setViewPost]       = useState(null);

  // Admin
  const [showAdmin, setShowAdmin]     = useState(false);
  const [isLoggedIn, setIsLoggedIn]   = useState(false);
  const [loginUser, setLoginUser]     = useState('');
  const [loginPass, setLoginPass]     = useState('');
  const [loginError, setLoginError]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Form
  const [form, setForm]               = useState(emptyForm);
  const [coverFile, setCoverFile]     = useState(null); // real File object, only set when a NEW image is chosen
  const [editId, setEditId]           = useState(null);
  const [formError, setFormError]     = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [activeTab, setActiveTab]     = useState('compose'); // compose | manage

  // ── fetch posts from the database ──
  const fetchPosts = async () => {
    setListLoading(true);
    setListError('');
    try {
      const response = await fetch(LIST_API_URL);
      const data = await response.json();
      if (data.success) {
        setPosts(data.posts);
      } else {
        setListError('Could not load posts.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // ── filtered ──
  const filtered = posts
    .filter(p => {
      const matchCat  = filterCat  === 'All' || p.category === filterCat;
      const matchType = filterType === 'All' || p.type === filterType;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || p.title.toLowerCase().includes(q) ||
        p.summary.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
      return matchCat && matchType && matchSearch;
    })
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const featured = filtered.find(p => p.pinned) || filtered[0] || null;
  const rest     = featured ? filtered.filter(p => p.id !== featured.id) : filtered;

  // ── login (real, server-verified) ──
  const handleLogin = async () => {
    setLoginError('');
    if (!loginUser || !loginPass) {
      setLoginError('Please enter both username and password.');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await fetch(LOGIN_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setIsLoggedIn(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid username or password.');
      }
    } catch (err) {
      setLoginError(`Unable to reach the server: ${err.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); setLoginUser(''); setLoginPass('');
    setShowAdmin(false); setForm(emptyForm); setEditId(null);
    setCoverFile(null); setCoverPreview(''); setFormError(''); setFormSuccess('');
  };

  // ── cover image (kept as a real File until submit, only previewed locally) ──
  const handleCoverFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setFormError('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setFormError('Cover image must be under 5 MB.'); return; }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setFormError('');
  };

  // ── submit (create or update on the server) ──
  const handleSubmit = async () => {
    setFormError('');
    if (!form.title.trim())   { setFormError('Title is required.'); return; }
    if (!form.category)       { setFormError('Please select a category.'); return; }
    if (!form.summary.trim()) { setFormError('Summary is required.'); return; }
    if (!form.body.trim())    { setFormError('Full content is required.'); return; }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('category', form.category);
      formData.append('type', form.type);
      formData.append('date', form.date);
      formData.append('eventDate', form.eventDate);
      formData.append('venue', form.venue);
      formData.append('summary', form.summary.trim());
      formData.append('body', form.body.trim());
      formData.append('tags', form.tags);
      formData.append('pinned', form.pinned ? 'true' : 'false');
      formData.append('username', loginUser);
      formData.append('password', loginPass);
      if (coverFile) {
        formData.append('coverImage', coverFile);
      }

      let response;
      if (editId) {
        formData.append('id', editId);
        formData.append('existingCoverImage', form.coverImage || '');
        response = await fetch(UPDATE_API_URL, { method: 'POST', body: formData });
      } else {
        response = await fetch(CREATE_API_URL, { method: 'POST', body: formData });
      }

      const data = await response.json();

      if (data.success) {
        setFormSuccess(editId ? 'Post updated successfully!' : 'Post published successfully!');
        setForm(emptyForm); setEditId(null); setCoverFile(null); setCoverPreview('');
        setTimeout(() => setFormSuccess(''), 3500);
        setActiveTab('manage');
        fetchPosts(); // refresh so the change shows up immediately
      } else {
        setFormError(data.message || 'Something went wrong.');
      }
    } catch (err) {
      setFormError(`Unable to reach the server: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (post) => {
    setForm({
      title: post.title, category: post.category, type: post.type,
      date: post.date || '', eventDate: post.eventDate || '',
      venue: post.venue || '', summary: post.summary, body: post.body,
      coverImage: post.coverImage || '', tags: (post.tags || []).join(', '),
      pinned: post.pinned || false,
    });
    setCoverFile(null);
    setCoverPreview(post.coverImage || '');
    setEditId(post.id);
    setActiveTab('compose');
  };

  // ── delete (real, server-verified) ──
  const handleDelete = async () => {
    try {
      const response = await fetch(DELETE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deleteId, username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setPosts(prev => prev.filter(p => p.id !== deleteId));
        if (viewPost?.id === deleteId) setViewPost(null);
      } else {
        setListError(data.message || 'Could not delete this post.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setDeleteId(null);
    }
  };

  // ── pin/unpin (real, server-verified) ──
  const togglePin = async (id) => {
    const post = posts.find(p => p.id === id);
    if (!post) return;
    const newPinned = !post.pinned;

    // Optimistic UI update, then confirm with the server
    setPosts(prev => prev.map(p => p.id === id ? { ...p, pinned: newPinned } : p));

    try {
      const response = await fetch(TOGGLE_PIN_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, pinned: newPinned, username: loginUser, password: loginPass }),
      });
      const data = await response.json();
      if (!data.success) {
        // revert on failure
        setPosts(prev => prev.map(p => p.id === id ? { ...p, pinned: !newPinned } : p));
      }
    } catch (err) {
      setPosts(prev => prev.map(p => p.id === id ? { ...p, pinned: !newPinned } : p));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const catColor = (cat) => CATEGORY_COLORS[cat] || '#555';

  const catCounts = CATEGORY_OPTIONS.reduce((acc, c) => {
    acc[c] = c === 'All' ? posts.length : posts.filter(p => p.category === c).length;
    return acc;
  }, {});

  // ── field helper ──
  const F = (field) => ({
    value: form[field],
    onChange: e => setForm(f => ({ ...f, [field]: e.target.value })),
  });

  return (
    <div className="news-page">
      <Navbar />

      {/* ── HERO ── */}
      <section className="news-hero">
        <div className="news-hero__deco">
          {[...Array(5)].map((_, i) => <div className="news-hero__ring" key={i} style={{ '--i': i }} />)}
        </div>
        <div className="news-hero__content">
          <span className="news-eyebrow">📰 Stay Informed</span>
          <h1>News &amp; Events</h1>
          <p>Latest announcements, happenings, achievements and upcoming events from the school.</p>
          <div className="news-hero__stats">
            <div className="news-hstat"><strong>{posts.length}</strong><span>Articles</span></div>
            <div className="news-hstat-div" />
            <div className="news-hstat"><strong>{posts.filter(p => p.type === 'Event').length}</strong><span>Events</span></div>
            <div className="news-hstat-div" />
            <div className="news-hstat"><strong>{[...new Set(posts.map(p => p.category))].length}</strong><span>Topics</span></div>
          </div>
        </div>
        <button className="news-admin-btn" onClick={() => setShowAdmin(true)}>
          {isLoggedIn ? '⚙️ Admin Panel' : '🔐 Admin Login'}
        </button>
      </section>

      {/* ── FILTER BAR ── */}
      <section className="news-filterbar">
        <div className="news-filterbar__cats">
          {CATEGORY_OPTIONS.filter(c => catCounts[c] > 0 || c === 'All').map(cat => (
            <button
              key={cat}
              className={`ncat-pill ${filterCat === cat ? 'ncat-pill--active' : ''}`}
              onClick={() => setFilterCat(cat)}
            >
              {cat} <span>{catCounts[cat]}</span>
            </button>
          ))}
        </div>
        <div className="news-filterbar__right">
          <div className="news-search">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Search news..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && <button onClick={() => setSearchQuery('')}>✕</button>}
          </div>
          <div className="type-toggle">
            {['All', 'News', 'Event'].map(t => (
              <button
                key={t}
                className={filterType === t ? 'active' : ''}
                onClick={() => setFilterType(t)}
              >{t}</button>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <section className="news-body">
        {listError && (
          <div className="news-empty">
            <span>⚠️</span>
            <h3>Something went wrong</h3>
            <p>{listError}</p>
          </div>
        )}

        {!listError && listLoading && (
          <div className="news-empty">
            <span>⏳</span>
            <h3>Loading posts...</h3>
          </div>
        )}

        {!listError && !listLoading && filtered.length === 0 && (
          <div className="news-empty">
            <span>📭</span>
            <h3>{posts.length === 0 ? 'No Posts Yet' : 'No Results Found'}</h3>
            <p>{posts.length === 0
              ? 'Admin can publish news and events from the Admin Panel.'
              : 'Try adjusting your filters or search query.'}</p>
          </div>
        )}

        {!listError && !listLoading && filtered.length > 0 && (
          <>
            {/* Featured */}
            {featured && (filterCat === 'All' && filterType === 'All' && !searchQuery) && (
              <div className="news-featured" onClick={() => setViewPost(featured)}>
                <div className="news-featured__img">
                  {featured.coverImage
                    ? <img src={featured.coverImage} alt={featured.title} />
                    : <div className="news-featured__placeholder">
                        <span style={{ fontSize: 64 }}>
                          {featured.type === 'Event' ? '📅' : '📰'}
                        </span>
                      </div>
                  }
                  <div className="news-featured__badge">
                    <span className="ncategory-tag" style={{ '--cat-color': catColor(featured.category) }}>
                      {featured.category}
                    </span>
                    {featured.pinned && <span className="pin-badge">📌 Featured</span>}
                  </div>
                </div>
                <div className="news-featured__body">
                  <span className="news-featured__type">{featured.type}</span>
                  <h2>{featured.title}</h2>
                  <p>{featured.summary}</p>
                  <div className="news-featured__meta">
                    {featured.date && <span>🗓 {formatDate(featured.date)}</span>}
                    {featured.venue && <span>📍 {featured.venue}</span>}
                    {featured.tags?.length > 0 && (
                      <div className="news-tags">
                        {featured.tags.slice(0, 3).map(t => <span key={t} className="news-tag">#{t}</span>)}
                      </div>
                    )}
                  </div>
                  <button className="news-featured__read">Read Full Article →</button>
                  {isLoggedIn && (
                    <div className="news-admin-actions" onClick={e => e.stopPropagation()}>
                      <button onClick={() => togglePin(featured.id)}>
                        {featured.pinned ? '📌 Unpin' : '📌 Pin'}
                      </button>
                      <button onClick={() => { startEdit(featured); setShowAdmin(true); }}>✏️ Edit</button>
                      <button className="danger" onClick={() => setDeleteId(featured.id)}>🗑️ Delete</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cards grid */}
            <div className="news-grid">
              {(featured && filterCat === 'All' && filterType === 'All' && !searchQuery ? rest : filtered).map((post, i) => (
                <article
                  className="news-card"
                  key={post.id}
                  style={{ animationDelay: `${Math.min(i * 0.06, 0.5)}s` }}
                  onClick={() => setViewPost(post)}
                >
                  <div className="news-card__img">
                    {post.coverImage
                      ? <img src={post.coverImage} alt={post.title} />
                      : <div className="news-card__no-img">
                          <span>{post.type === 'Event' ? '📅' : '📰'}</span>
                        </div>
                    }
                    <span className="ncategory-tag news-card__cat" style={{ '--cat-color': catColor(post.category) }}>
                      {post.category}
                    </span>
                    {post.pinned && <span className="card-pin">📌</span>}
                  </div>
                  <div className="news-card__body">
                    <div className="news-card__type-row">
                      <span className={`news-type-badge news-type-badge--${post.type.toLowerCase()}`}>{post.type}</span>
                      {post.date && <span className="news-card__date">{formatDate(post.date)}</span>}
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.summary}</p>
                    {post.type === 'Event' && post.eventDate && (
                      <div className="news-card__event-info">
                        <span>📅 {formatDate(post.eventDate)}</span>
                        {post.venue && <span>📍 {post.venue}</span>}
                      </div>
                    )}
                    {post.tags?.length > 0 && (
                      <div className="news-tags">
                        {post.tags.slice(0, 3).map(t => <span key={t} className="news-tag">#{t}</span>)}
                      </div>
                    )}
                    <div className="news-card__footer">
                      <span className="news-card__read-link">Read More →</span>
                      {isLoggedIn && (
                        <div className="news-admin-actions" onClick={e => e.stopPropagation()}>
                          <button onClick={() => togglePin(post.id)} title={post.pinned ? 'Unpin' : 'Pin'}>
                            {post.pinned ? '📌' : '📍'}
                          </button>
                          <button onClick={() => { startEdit(post); }}>✏️</button>
                          <button className="danger" onClick={() => setDeleteId(post.id)}>🗑️</button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ══════════════════════════════════════
          ARTICLE VIEW MODAL
      ══════════════════════════════════════ */}
      {viewPost && (
        <div className="modal-overlay" onClick={() => setViewPost(null)}>
          <div className="modal modal--article" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setViewPost(null)}>✕</button>
            {viewPost.coverImage && (
              <div className="article-cover">
                <img src={viewPost.coverImage} alt={viewPost.title} />
              </div>
            )}
            <div className="article-content">
              <div className="article-meta-top">
                <span className="ncategory-tag" style={{ '--cat-color': catColor(viewPost.category) }}>
                  {viewPost.category}
                </span>
                <span className={`news-type-badge news-type-badge--${viewPost.type.toLowerCase()}`}>
                  {viewPost.type}
                </span>
                {viewPost.pinned && <span className="pin-badge">📌 Featured</span>}
              </div>
              <h2 className="article-title">{viewPost.title}</h2>
              <div className="article-info-row">
                {viewPost.date && <span>🗓 Published: {formatDate(viewPost.date)}</span>}
                {viewPost.eventDate && <span>📅 Event Date: {formatDate(viewPost.eventDate)}</span>}
                {viewPost.venue && <span>📍 {viewPost.venue}</span>}
              </div>
              <p className="article-summary">{viewPost.summary}</p>
              <div className="article-body">
                {viewPost.body.split('\n').filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {viewPost.tags?.length > 0 && (
                <div className="article-tags">
                  {viewPost.tags.map(t => <span key={t} className="news-tag">#{t}</span>)}
                </div>
              )}
              <div className="article-footer-meta">
                <span>Posted: {formatDate(viewPost.createdAt)}</span>
                {viewPost.updatedAt && <span>Updated: {formatDate(viewPost.updatedAt)}</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          ADMIN MODAL
      ══════════════════════════════════════ */}
      {showAdmin && (
        <div className="modal-overlay" onClick={e => { if (e.target.classList.contains('modal-overlay') && !isLoggedIn) setShowAdmin(false); }}>
          <div className="modal modal--admin">

            {/* Login */}
            {!isLoggedIn ? (
              <div className="modal-login">
                <button className="modal-close" onClick={() => setShowAdmin(false)}>✕</button>
                <div className="modal-login__icon">🔐</div>
                <h2>Admin Login</h2>
                <p>Sign in to manage news &amp; events</p>
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" placeholder="Username" value={loginUser}
                    onChange={e => setLoginUser(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input type="password" placeholder="Password" value={loginPass}
                    onChange={e => setLoginPass(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                </div>
                {loginError && <div className="form-error">{loginError}</div>}
                <button className="modal-login__btn" onClick={handleLogin} disabled={loginLoading}>
                  {loginLoading ? 'Logging in...' : 'Login →'}
                </button>
              </div>
            ) : (
              /* Admin panel */
              <div className="modal-admin">
                <div className="modal-admin__header">
                  <div>
                    <h2>News &amp; Events Admin</h2>
                    <p>Publish and manage posts</p>
                  </div>
                  <div className="modal-admin__header-actions">
                    <span className="admin-badge">✅ {loginUser}</span>
                    <button className="btn-logout" onClick={handleLogout}>Logout</button>
                    <button className="modal-close" onClick={() => setShowAdmin(false)}>✕</button>
                  </div>
                </div>

                {/* Summary */}
                <div className="admin-summary">
                  {[
                    { val: posts.length,                                 label: 'Total Posts' },
                    { val: posts.filter(p => p.type === 'News').length,  label: 'News' },
                    { val: posts.filter(p => p.type === 'Event').length, label: 'Events' },
                    { val: posts.filter(p => p.pinned).length,           label: 'Pinned' },
                  ].map(s => (
                    <div className="admin-summary__card" key={s.label}>
                      <span>{s.val}</span><label>{s.label}</label>
                    </div>
                  ))}
                </div>

                {/* Tabs */}
                <div className="admin-tabs">
                  <button className={activeTab === 'compose' ? 'active' : ''} onClick={() => { setActiveTab('compose'); setForm(emptyForm); setEditId(null); setCoverFile(null); setCoverPreview(''); }}>
                    {editId ? '✏️ Edit Post' : '✍️ New Post'}
                  </button>
                  <button className={activeTab === 'manage' ? 'active' : ''} onClick={() => setActiveTab('manage')}>
                    📋 Manage ({posts.length})
                  </button>
                </div>

                <div className="modal-admin__body">

                  {/* ── COMPOSE TAB ── */}
                  {activeTab === 'compose' && (
                    <div className="compose-form">
                      <div className="form-row">
                        <div className="form-group form-group--full">
                          <label>Title *</label>
                          <input type="text" placeholder="Enter a compelling headline..." {...F('title')} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Type *</label>
                          <select {...F('type')}>
                            {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Category *</label>
                          <select {...F('category')}>
                            <option value="">Select Category</option>
                            {CATEGORY_OPTIONS.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Publish Date</label>
                          <input type="date" {...F('date')} />
                        </div>
                        {form.type === 'Event' && (
                          <div className="form-group">
                            <label>Event Date</label>
                            <input type="date" {...F('eventDate')} />
                          </div>
                        )}
                        {form.type === 'Event' && (
                          <div className="form-group">
                            <label>Venue</label>
                            <input type="text" placeholder="e.g. School Auditorium" {...F('venue')} />
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Summary * <small>(shown on card)</small></label>
                        <textarea placeholder="Brief description visible on the news card..." rows={3} {...F('summary')} />
                      </div>
                      <div className="form-group">
                        <label>Full Content * <small>(full article, press Enter for paragraphs)</small></label>
                        <textarea placeholder="Write the complete article or event details here..." rows={7} {...F('body')} />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Tags <small>(comma-separated)</small></label>
                          <input type="text" placeholder="e.g. sports, annual day, cbse" {...F('tags')} />
                        </div>
                        <div className="form-group">
                          <label>Cover Image <small>(optional, max 5 MB)</small></label>
                          <input type="file" accept="image/*" onChange={handleCoverFile} />
                        </div>
                      </div>
                      {coverPreview && (
                        <div className="cover-preview">
                          <img src={coverPreview} alt="cover preview" />
                          <button onClick={() => { setCoverFile(null); setCoverPreview(''); setForm(f => ({ ...f, coverImage: '' })); }}>
                            ✕ Remove
                          </button>
                        </div>
                      )}
                      <div className="form-group form-group--checkbox">
                        <input
                          type="checkbox"
                          id="pinned"
                          checked={form.pinned}
                          onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))}
                        />
                        <label htmlFor="pinned">📌 Pin this post as Featured</label>
                      </div>
                      {formError   && <div className="form-error">{formError}</div>}
                      {formSuccess && <div className="form-success">{formSuccess}</div>}
                      <div className="compose-actions">
                        {editId && (
                          <button className="btn-secondary" onClick={() => { setForm(emptyForm); setEditId(null); setCoverFile(null); setCoverPreview(''); }}>
                            Cancel Edit
                          </button>
                        )}
                        <button className="btn-publish" onClick={handleSubmit} disabled={isSubmitting}>
                          {isSubmitting ? 'Saving...' : (editId ? '💾 Save Changes' : '🚀 Publish Post')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── MANAGE TAB ── */}
                  {activeTab === 'manage' && (
                    <div className="manage-list">
                      {posts.length === 0 ? (
                        <div className="manage-empty">
                          <p>No posts yet. Go to <button onClick={() => setActiveTab('compose')}>New Post</button> to publish one.</p>
                        </div>
                      ) : (
                        posts.map(post => (
                          <div className="manage-item" key={post.id}>
                            <div className="manage-item__thumb">
                              {post.coverImage
                                ? <img src={post.coverImage} alt="" />
                                : <span>{post.type === 'Event' ? '📅' : '📰'}</span>
                              }
                            </div>
                            <div className="manage-item__info">
                              <div className="manage-item__top">
                                <span className="ncategory-tag ncategory-tag--sm" style={{ '--cat-color': catColor(post.category) }}>
                                  {post.category}
                                </span>
                                <span className={`news-type-badge news-type-badge--${post.type.toLowerCase()} news-type-badge--sm`}>
                                  {post.type}
                                </span>
                                {post.pinned && <span className="pin-badge pin-badge--sm">📌</span>}
                              </div>
                              <strong>{post.title}</strong>
                              <span>{formatDate(post.createdAt)}</span>
                            </div>
                            <div className="manage-item__actions">
                              <button onClick={() => togglePin(post.id)} title={post.pinned ? 'Unpin' : 'Pin'}>
                                {post.pinned ? '📌' : '📍'}
                              </button>
                              <button onClick={() => { startEdit(post); }}>✏️</button>
                              <button className="danger" onClick={() => setDeleteId(post.id)}>🗑️</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal modal--confirm">
            <div className="confirm-icon">⚠️</div>
            <h3>Delete Post?</h3>
            <p>This action cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-delete" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsEvents;